import { SocialMediaType } from 'api/graphql-global-types';

/**
 * Kept in lockstep with `src/utils/validations/validateSocialLink.ts` (athlete-mobile-app) and
 * `shared/validators/is-social-media-profile-link.validator.ts` (fs-backend). Change all three.
 */

/** Hosts a profile link may point at, per platform. `www.` prefixes are accepted for all of them. */
const PROFILE_HOSTS: Record<SocialMediaType, string[]> = {
  [SocialMediaType.Instagram]: ['instagram.com', 'instagr.am'],
  [SocialMediaType.Facebook]: ['facebook.com', 'fb.com', 'fb.me'],
  [SocialMediaType.Twitter]: ['twitter.com', 'x.com'],
  [SocialMediaType.Tiktok]: ['tiktok.com'],
  [SocialMediaType.Youtube]: ['youtube.com', 'youtu.be'],
};

/**
 * First path segments the platforms reserve for content rather than profiles, so a pasted post or
 * video URL is not mistaken for one. Profile-bearing prefixes (`youtube.com/channel`, `/c`,
 * `/user`, `facebook.com/pages`) are deliberately absent.
 */
const RESERVED_PATHS: Record<SocialMediaType, string[]> = {
  [SocialMediaType.Instagram]: [
    'p',
    'reel',
    'reels',
    'tv',
    'stories',
    'explore',
  ],
  [SocialMediaType.Facebook]: ['watch', 'groups', 'events', 'marketplace'],
  [SocialMediaType.Twitter]: [
    'i',
    'home',
    'search',
    'hashtag',
    'explore',
    'intent',
  ],
  [SocialMediaType.Tiktok]: [
    'video',
    'tag',
    'discover',
    'music',
    'effect',
    'live',
  ],
  [SocialMediaType.Youtube]: [
    'watch',
    'shorts',
    'playlist',
    'results',
    'feed',
    'embed',
    'hashtag',
  ],
};

/**
 * Handles shorter than this are rejected. Single-character profiles are technically possible on
 * some platforms but vanishingly rare, and allowing them lets junk like `x.com/o` through.
 */
const MIN_HANDLE_LENGTH = 2;

const toUrl = (value: string): URL | null => {
  if (!value) {
    return null;
  }

  try {
    return new URL(/^https?:\/\//i.test(value) ? value : `https://${value}`);
  } catch {
    return null;
  }
};

const toHandle = (pathname: string): string => {
  try {
    return (
      decodeURIComponent(pathname)
        .split('/')
        .filter(Boolean)[0]
        ?.replace(/^@/, '') ?? ''
    );
  } catch {
    return '';
  }
};

/**
 * True when `link` points at a profile on `platform` — right host, and a handle in the path.
 * Deliberately does not check that the profile exists; the backend data pull handles that.
 */
export const isSocialProfileLink = (
  platform: SocialMediaType,
  link?: string | null
): boolean => {
  const hosts = PROFILE_HOSTS[platform];

  if (!hosts || typeof link !== 'string') {
    return false;
  }

  const url = toUrl(link.trim());

  if (!url) {
    return false;
  }

  const host = url.hostname.toLowerCase().replace(/^www\./, '');

  if (!hosts.includes(host)) {
    return false;
  }

  const handle = toHandle(url.pathname);

  return (
    handle.length >= MIN_HANDLE_LENGTH &&
    !RESERVED_PATHS[platform].includes(handle.toLowerCase())
  );
};

export const getSocialProfileLinkExample = (
  platform: SocialMediaType
): string => `https://${PROFILE_HOSTS[platform]?.[0]}/yourname`;

/**
 * Error message for a link the user has already typed, or `undefined` when it is a usable profile
 * link. Empty values return `undefined` — "required" is reported separately so the field does not
 * show an error before the user types anything.
 */
export const getSocialProfileLinkError = (
  platform?: SocialMediaType,
  link?: string | null
): string | undefined => {
  if (!platform || !link?.trim()) {
    return undefined;
  }

  return isSocialProfileLink(platform, link)
    ? undefined
    : `Enter a link to your profile, e.g. ${getSocialProfileLinkExample(
        platform
      )}`;
};
